import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trophy, MapPin, Calendar, Users, Plus, ArrowRight, Building2, Trash2 } from "lucide-react";

export default function Ops() {
  const { user } = useAuth();
  const isOrg = user?.role === "organisation";
  const [tours, setTours] = useState(null);
  const [catalog, setCatalog] = useState({ sports: [] });

  const load = () => api.get("/tournaments").then((r) => setTours(r.data.tournaments)).catch(() => setTours(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  return (
    <div className="fade-up">
      <PageHeader overline="Operational workforce deployment" title="Krida Ops"
        subtitle="Tournaments, workforce requirements, rostering, attendance and the live command centre."
        actions={isOrg && <CreateTournament catalog={catalog} onCreated={load} />}
        testid="krida-ops-page" />

      {tours === null ? <Loader /> : tours.length === 0 ? <Empty label="No tournaments yet" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {tours.map((t) => {
            const req = t.requirements.reduce((s, r) => s + r.required, 0);
            const conf = t.requirements.reduce((s, r) => s + r.confirmed, 0);
            const repl = t.requirements.reduce((s, r) => s + r.replacement_needed, 0);
            return (
              <Link to={`/app/ops/${t.id}`} key={t.id} className="krida-card p-6 hover:-translate-y-0.5 transition-all" data-testid={`ops-tournament-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-krida-amber/10 grid place-items-center"><Trophy className="w-5 h-5 text-krida-amber" /></div>
                    <div><div className="font-heading text-xl font-bold leading-tight">{t.name}</div><div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" /> {t.org_name}</div></div>
                  </div>
                  <Tag tone={statusTone(t.status)}>{t.status}</Tag>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {t.venue}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(t.start_date).toLocaleDateString()}</span>
                </div>
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm"><Users className="w-4 h-4 text-krida-emerald" /><span className="font-mono text-krida-emerald">{conf}</span><span className="text-slate-500">/ {req} confirmed</span></div>
                  {repl > 0 && <Tag tone="crimson">{repl} replacement</Tag>}
                  <ArrowRight className="w-4 h-4 text-slate-600" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CreateTournament({ catalog, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", sport: "Basketball", event_type: "Championship", city: "Chennai",
    state: "Tamil Nadu", venue: "", start_date: "", end_date: "", expected_participants: 200, budget: 500000 });
  const [reqs, setReqs] = useState([{ role: "Referee", required: 12 }, { role: "Physiotherapist", required: 4 }, { role: "Volunteer", required: 20 }]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setReq = (i, k, v) => setReqs((r) => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const submit = async () => {
    if (!f.name || !f.start_date || !f.end_date) { toast.error("Name and dates required"); return; }
    try {
      await api.post("/tournaments", { ...f, expected_participants: Number(f.expected_participants), budget: Number(f.budget),
        requirements: reqs.map((r) => ({ role: r.role, required: Number(r.required) })) });
      toast.success("Tournament created"); setOpen(false); onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="create-tournament-btn" className="px-4 py-2.5 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold text-sm flex items-center gap-2 transition-colors"><Plus className="w-4 h-4" /> New tournament</button>
      </DialogTrigger>
      <DialogContent className="bg-krida-card border-white/10 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Create tournament</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <Inp label="Tournament name" value={f.name} onChange={(v) => set("name", v)} testid="tour-name" />
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Sport" value={f.sport} onChange={(v) => set("sport", v)} options={catalog.sports.map((s) => s.name)} testid="tour-sport" />
            <Inp label="Venue" value={f.venue} onChange={(v) => set("venue", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Start date" type="date" value={f.start_date} onChange={(v) => set("start_date", v)} testid="tour-start" />
            <Inp label="End date" type="date" value={f.end_date} onChange={(v) => set("end_date", v)} testid="tour-end" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Inp label="City" value={f.city} onChange={(v) => set("city", v)} />
            <Inp label="Budget (₹)" type="number" value={f.budget} onChange={(v) => set("budget", v)} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2"><label className="overline text-slate-500">Workforce requirements</label>
              <button onClick={() => setReqs((r) => [...r, { role: "Event Personnel", required: 5 }])} className="text-xs text-krida-crimson">+ Add role</button></div>
            <div className="space-y-2">
              {reqs.map((r, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <div className="flex-1"><Sel value={r.role} onChange={(v) => setReq(i, "role", v)} options={["Referee", "Umpire", "Table Official", "Match Commissioner", "Physiotherapist", "Competition Manager", "Event Personnel", "Volunteer", "Logistics Coordinator"]} /></div>
                  <input type="number" value={r.required} onChange={(e) => setReq(i, "required", e.target.value)} className="w-20 px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none" />
                  <button onClick={() => setReqs((x) => x.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-krida-crimson"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <button onClick={submit} data-testid="tour-submit" className="w-full py-3 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold transition-colors">Create tournament</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Inp({ label, value, onChange, type = "text", testid }) {
  return (<div><label className="overline text-slate-500 block mb-1">{label}</label>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 focus:border-krida-crimson/60 outline-none text-sm" /></div>);
}
function Sel({ label, value, onChange, options, testid }) {
  return (<div>{label && <label className="overline text-slate-500 block mb-1">{label}</label>}
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-black/40 border-white/10 rounded-xl text-sm w-full"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-krida-card border-white/10 text-slate-100 max-h-64">{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select></div>);
}
